// src/pages/CompanySetup.js
import Lottie from "lottie-react";
import loadingAnimation from "../tools/Animations/Sandy Loading.json";
import { trackEvent } from "../firebase/analytics";
import { useRef } from "react";
import gsap from "gsap";
import { CheckCircle2, Circle } from "lucide-react";

import AddressAutocomplete from "../components/AddressAutocomplete";

import StatusBanner from "../components/StatusBanner";
import { formatError } from "../utils/errorMessages";
import { parsePlace } from "../utils/parsePlace";
import { isPilotEmail, ensureCompanyPilot } from "../utils/pilot";

import { useState, useEffect, useMemo } from "react";
import { registerCompany } from "../firebase/company";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import { useAuth } from "../Context/AuthContext";
import {
  doc,
  updateDoc,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import Select from "react-select";
import "../styles/CompanySetup.css";
import Step1Illo from "../tools/assets/undraw_building_burz.svg";
import Step2Illo from "../tools/assets/undraw_doctors_djoj.svg";
import Step3Illo from "../tools/assets/undraw_team_85hs (1).svg";
import Step4Illo from "../tools/assets/undraw_best-place_dhzp.svg";
import Step5Illo from "../tools/assets/undraw_connected-world_anke (2).svg";
import Step6Illo from "../tools/assets/undraw_global-team_8jok.svg";

// ---------- Options ----------
const industryOptions = [
  { value: "Technology", label: "Technology" },
  { value: "Healthcare", label: "Healthcare" },
  { value: "Finance", label: "Finance" },
  { value: "Security", label: "Security / Guard Services" },
  { value: "Retail", label: "Retail / Store" },
  { value: "Hospitality", label: "Hospitality / Hotel" },
  { value: "Restaurant", label: "Restaurant / Cafe" },
  { value: "Salon", label: "Salon / Barber Shop / Spa" },
  { value: "Fitness", label: "Fitness / Gym" },
  { value: "Automotive", label: "Automotive / Auto Repair" },
  { value: "Cleaning", label: "Cleaning Services" },
  { value: "Construction", label: "Construction" },
  { value: "Manufacturing", label: "Manufacturing" },
  { value: "Transportation", label: "Transportation / Logistics" },
  { value: "Real Estate", label: "Real Estate" },
  { value: "Education", label: "Education / Training" },
  { value: "Government", label: "Government" },
  { value: "Nonprofit", label: "Nonprofit" },
  { value: "Other", label: "Other" },
];

const timeZoneOptions = [
  { value: "America/New_York", label: "Eastern Time (US & Canada)" },
  { value: "America/Chicago", label: "Central Time (US & Canada)" },
  { value: "America/Denver", label: "Mountain Time (US & Canada)" },
  { value: "America/Phoenix", label: "Arizona (No DST)" },
  { value: "America/Los_Angeles", label: "Pacific Time (US & Canada)" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "Pacific/Honolulu", label: "Hawaii Time" },

  { value: "America/Puerto_Rico", label: "Atlantic Time (Puerto Rico)" },
  { value: "America/St_Thomas", label: "Atlantic Time (U.S. Virgin Islands)" },
  { value: "Pacific/Guam", label: "Chamorro Time (Guam)" },
  { value: "Pacific/Pago_Pago", label: "Samoa Time (American Samoa)" },

  { value: "Europe/London", label: "London (UK)" },
  { value: "Europe/Dublin", label: "Ireland (Dublin)" },
  { value: "Europe/Paris", label: "Central European Time (France, Germany)" },
  { value: "Europe/Berlin", label: "Berlin (Germany)" },
  { value: "Europe/Madrid", label: "Madrid (Spain)" },
  { value: "Europe/Istanbul", label: "Istanbul (Turkey)" },
  { value: "Europe/Moscow", label: "Moscow (Russia)" },

  { value: "Africa/Lagos", label: "West Africa Time (Nigeria)" },
  { value: "Africa/Cairo", label: "Eastern European Time (Egypt)" },
  { value: "Africa/Nairobi", label: "East Africa Time (Kenya)" },
  { value: "Africa/Johannesburg", label: "South Africa Standard Time" },

  { value: "Asia/Karachi", label: "Pakistan Standard Time (PKT)" },
  { value: "Asia/Kolkata", label: "India Standard Time (IST)" },
  { value: "Asia/Dhaka", label: "Bangladesh Standard Time (BST)" },
  { value: "Asia/Shanghai", label: "China Standard Time (CST)" },
  { value: "Asia/Hong_Kong", label: "Hong Kong Time" },
  { value: "Asia/Singapore", label: "Singapore Standard Time" },
  { value: "Asia/Tokyo", label: "Japan Standard Time (JST)" },
  { value: "Asia/Seoul", label: "Korea Standard Time (KST)" },

  { value: "Australia/Sydney", label: "AET (Sydney/Melbourne)" },
  { value: "Australia/Hobart", label: "AET (Hobart)" },
  { value: "Australia/Brisbane", label: "AET (Brisbane, no DST)" },
  { value: "Australia/Adelaide", label: "ACST (Adelaide)" },
  { value: "Australia/Darwin", label: "ACST (Darwin, no DST)" },
  { value: "Australia/Perth", label: "AWST (Perth)" },
  { value: "Pacific/Auckland", label: "New Zealand (Auckland)" },
  { value: "Pacific/Chatham", label: "New Zealand (Chatham, +45m)" },

  { value: "America/Bogota", label: "Colombia Time" },
  { value: "America/Sao_Paulo", label: "Brazil Standard Time" },
  { value: "America/Argentina/Buenos_Aires", label: "Argentina Time" },

  { value: "Etc/UTC", label: "UTC (Coordinated Universal Time)" },
];

const teamSizeToMax = (val) => {
  const map = {
    "1-3": 3,
    "4-10": 10,
    "11-20": 20,
    "21-35": 35,
    "36-50": 50,
  };
  return map[val] ?? 3;
};

const teamSizeOptions = [
  { value: "1-3", label: "1–3 Employees — Free plan available" },
  { value: "4-10", label: "4–10 Employees" },
  { value: "11-20", label: "11–20 Employees" },
  { value: "21-35", label: "21–35 Employees" },
  { value: "36-50", label: "36–50 Employees" },
];
const DRAFT_KEY = "companySetupDraft_v1";

const SETUP_STEP_MS = 2000; // ✅ try 800–1200 for “slower”
const FINAL_STEP_EXTRA_MS = 1000;

// ✅ Setup sequence (overlay messages)
const SETUP_SEQUENCE = [
  "Setting up Oiiko for your team…",
  "Building your dashboard…",
  "Setting up your team workflows…",
  "Preparing reports…",

  "Your workspace is ready.",
];

// ✅ Step illustrations (testing with one SVG for now)
const STEP_ILLUSTRATIONS = {
  1: Step1Illo, // company name
  2: Step2Illo, // industry
  3: Step3Illo, // team size
  4: Step4Illo, // address
  5: Step5Illo, // time zone
  6: Step6Illo, // multiple locations
};

const getIllustrationForStep = (step) => STEP_ILLUSTRATIONS[step] || Step1Illo;

export default function CompanySetup() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  // ---------- One-time hard refresh after verification -> navigation
  useEffect(() => {
    const needsRefresh = localStorage.getItem("needsCompanySetupRefresh");
    if (needsRefresh === "true") {
      localStorage.removeItem("needsCompanySetupRefresh");
      window.location.reload();
    }
  }, []);

  // ---------- (Optional) Re-validate on tab restore / BFCache
  useEffect(() => {
    const handler = async () => {
      try {
        // Optional: auth.currentUser?.reload()
      } catch {
        // ignore
      }
    };

    const onVis = () => {
      if (document.visibilityState === "visible") handler();
    };

    window.addEventListener("pageshow", handler);
    document.addEventListener("visibilitychange", onVis);

    return () => {
      window.removeEventListener("pageshow", handler);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // ---------- Wizard state
  const [step, setStep] = useState(1); // 1–6

  // ---------- Form state
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState(null);
  const [companySize, setCompanySize] = useState(null);

  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [coords, setCoords] = useState({ lat: null, lng: null });

  const [multiLocationEnabled, setMultiLocationEnabled] = useState(false);
  const [extraLocations, setExtraLocations] = useState([]);

  const [timeZone, setTimeZone] = useState(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const match = timeZoneOptions.find((t) => t.value === tz);
    return match || { value: tz, label: tz };
  });

  // ---------- UI state
  const [error, setError] = useState(null); // { title, message } or null
  const [loadingAnimationVisible, setLoadingAnimationVisible] = useState(false);
  // ✅ Setup overlay sequencing
  const [setupMsgIndex, setSetupMsgIndex] = useState(0);
  const [setupVisible, setSetupVisible] = useState(false);

  const contentRef = useRef(null);
  const heroImgRef = useRef(null);
  const stepChipRef = useRef(null);
  const locationsRef = useRef(null);

  // ---------- Effects (draft hydrate + persist)
  useEffect(() => {
    const draft = localStorage.getItem(DRAFT_KEY);
    if (!draft) return;

    try {
      const d = JSON.parse(draft);

      setStep(d.step ?? 1);
      setCompanyName(d.companyName || "");
      setIndustry(d.industry || null);
      setCompanySize(d.companySize || null);

      setStreet(d.street || "");
      setCity(d.city || "");
      setStateProvince(d.stateProvince || "");
      setPostalCode(d.postalCode || "");
      setCountry(d.country || "");

      if (d.timeZone) setTimeZone(d.timeZone);

      // optional: persist multi-location draft if you want
      if (typeof d.multiLocationEnabled === "boolean")
        setMultiLocationEnabled(d.multiLocationEnabled);
      if (Array.isArray(d.extraLocations)) setExtraLocations(d.extraLocations);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const draft = {
      step,
      companyName,
      industry,
      companySize,
      street,
      city,
      stateProvince,
      postalCode,
      country,
      timeZone,
      multiLocationEnabled,
      extraLocations,
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [
    step,
    companyName,
    industry,
    companySize,
    street,
    city,
    stateProvince,
    postalCode,
    country,
    timeZone,
    multiLocationEnabled,
    extraLocations,
  ]);

  // If your earlier logic wants to set a flag when user not ready yet
  useEffect(() => {
    if (!loading && !user) {
      localStorage.setItem("needsCompanySetupRefresh", "true");
    }
  }, [user, loading]);

  useEffect(() => {
    trackEvent("company_setup_view", {
      page: "company_setup",
    });
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    gsap.fromTo(
      contentRef.current,
      { opacity: 0, y: 10 },
      { opacity: 1, y: 0, duration: 0.35, ease: "power2.out" },
    );

    if (heroImgRef.current) {
      gsap.fromTo(
        heroImgRef.current,
        { opacity: 0, y: 6, scale: 0.98 },
        { opacity: 1, y: 0, scale: 1, duration: 0.4, ease: "power2.out" },
      );
    }

    if (stepChipRef.current) {
      gsap.fromTo(
        stepChipRef.current,
        { scale: 0.9 },
        { scale: 1, duration: 0.25, ease: "back.out(2)" },
      );
    }
  }, [step]);

  useEffect(() => {
    if (!multiLocationEnabled) return;
    if (!locationsRef.current) return;

    gsap.fromTo(
      locationsRef.current,
      { opacity: 0, y: 12 },
      { opacity: 1, y: 0, duration: 0.35, ease: "power2.out" },
    );
  }, [multiLocationEnabled]);

  useEffect(() => {
    if (!setupVisible) return;

    setSetupMsgIndex(0);

    const id = setInterval(() => {
      setSetupMsgIndex((i) => Math.min(i + 1, SETUP_SEQUENCE.length - 1));
    }, SETUP_STEP_MS);

    return () => clearInterval(id);
  }, [setupVisible]);

  // ---------- Validation (hooks must be before any early returns!)
  const step1Valid = useMemo(() => companyName.trim(), [companyName]); // name
  const step2Valid = useMemo(() => !!industry, [industry]); // industry
  const step3Valid = useMemo(() => !!companySize, [companySize]); // size

  const step4Valid = useMemo(
    () =>
      street.trim() &&
      city.trim() &&
      stateProvince.trim() &&
      postalCode.trim() &&
      country.trim(),
    [street, city, stateProvince, postalCode, country],
  ); // address

  const step5Valid = useMemo(() => !!timeZone, [timeZone]); // time zone

  // ---------- Early returns (after hooks)
  if (loading) return <p>Loading your account...</p>;
  if (!user) return null;

  // ---------- Navigation helpers
  const goNext = () => {
    setError(null);

    if (step === 1 && !step1Valid) {
      setError({
        title: "Company Name Needed",
        message: "Enter your company name to continue.",
      });
      return;
    }

    if (step === 2 && !step2Valid) {
      setError({
        title: "Select an Industry",
        message: "Choose the industry that best matches your business.",
      });
      return;
    }

    if (step === 3 && !step3Valid) {
      setError({
        title: "Choose Your Team Size",
        message: "Pick the range that fits your current team.",
      });
      return;
    }

    if (step === 4 && !step4Valid) {
      setError({
        title: "Complete Your Address",
        message: "Fill in your main office address to continue.",
      });
      return;
    }

    if (step === 5 && !step5Valid) {
      setError({
        title: "Choose a Time Zone",
        message: "Select the time zone your team operates in.",
      });
      return;
    }

    trackEvent("company_setup_step_continue", {
      from_step: step,
      to_step: Math.min(6, step + 1),
    });

    setStep((s) => Math.min(6, s + 1));
  };

  const goBack = () => {
    setError(null);
    trackEvent("company_setup_step_back", {
      from_step: step,
      to_step: Math.max(1, step - 1),
    });

    setStep((s) => Math.max(1, s - 1));
  };

  // helper: REST geocode for free-typed addresses
  const validateOrGeocodeAddress = async () => {
    if (coords?.lat && coords?.lng) {
      return {
        street,
        city,
        stateProvince,
        postalCode,
        country,
        lat: coords.lat,
        lng: coords.lng,
      };
    }

    const query = [street, city, stateProvince, postalCode, country]
      .filter(Boolean)
      .join(", ");

    if (!query) return null;

    try {
      const url = new URL("https://maps.googleapis.com/maps/api/geocode/json");
      url.searchParams.set("address", query);
      url.searchParams.set(
        "key",
        process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
      );

      const res = await fetch(url.toString());
      const data = await res.json();

      if (data.status !== "OK" || !data.results?.length) return null;

      const r = data.results[0];

      const comps = r.address_components || [];
      const get = (...types) =>
        comps.find((c) => c.types?.some((t) => types.includes(t)));

      const number = get("street_number")?.long_name || "";
      const route = get("route")?.long_name || "";
      const normalizedStreet =
        number && route ? `${number} ${route}` : route || street;

      return {
        street: normalizedStreet,
        city:
          get("locality")?.long_name ||
          get("sublocality")?.long_name ||
          get("administrative_area_level_2")?.long_name ||
          city,
        stateProvince:
          get("administrative_area_level_1")?.short_name ||
          get("administrative_area_level_1")?.long_name ||
          stateProvince,
        postalCode: get("postal_code")?.long_name || postalCode,
        country: get("country")?.long_name || country,
        lat: r.geometry?.location?.lat ?? null,
        lng: r.geometry?.location?.lng ?? null,
      };
    } catch {
      return null;
    }
  };
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));

  // ---------- Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    trackEvent("company_setup_submit_attempt", {
      page: "company_setup",
      team_size: companySize?.value || "unknown",
      industry: industry?.value || "unknown",
      multi_location: multiLocationEnabled,
    });

    // main company must have Step 2 complete
    if (!step4Valid || !step5Valid) {
      setError({
        title: "Almost there",
        message:
          "Please complete your address and time zone before continuing.",
      });
      return;
    }

    setSetupVisible(true);
    setLoadingAnimationVisible(true);
    setSetupMsgIndex(0);
    const overlayStart = Date.now();
    localStorage.setItem("oiiko_setup_in_progress", "true");
    localStorage.setItem("oiiko_setup_started_at", String(Date.now()));

    // ✅ Total time for the whole sequence to finish
    const SEQUENCE_TOTAL_MS =
      SETUP_STEP_MS * (SETUP_SEQUENCE.length - 1) + FINAL_STEP_EXTRA_MS;

    // ✅ Wait until the sequence time has completed
    const waitForSequenceToFinish = async () => {
      const elapsed = Date.now() - overlayStart;
      const remaining = Math.max(0, SEQUENCE_TOTAL_MS - elapsed);
      if (remaining) await wait(remaining);
    };

    try {
      const ownerId = user?.uid;

      if (!ownerId) throw new Error("User not authenticated");

      // ✅ Ensure address is valid even if user didn't select a suggestion
      const parsed = await validateOrGeocodeAddress();
      if (!parsed || !parsed.lat || !parsed.lng) {
        setLoadingAnimationVisible(false);
        setSetupVisible(false); // ✅ add this
        localStorage.removeItem("oiiko_setup_in_progress");
        localStorage.removeItem("oiiko_setup_started_at");
        trackEvent("company_setup_error", {
          error_code: "address_not_recognized",
          step: 6,
        });
        setError({
          title: "Address Not Recognized",
          message:
            "We couldn’t validate that address. Please pick a suggestion from the list or double-check the details and try again.",
        });
        return;
      }

      // Normalize fields prior to save (optional but recommended)
      const normalizedAddress = {
        street: parsed.street || street,
        city: parsed.city || city,
        stateProvince: parsed.stateProvince || stateProvince,
        postalCode: parsed.postalCode || postalCode,
        country: parsed.country || country,
        geo: { lat: parsed.lat, lng: parsed.lng },
      };

      // keep local UI in sync
      setStreet(normalizedAddress.street);
      setCity(normalizedAddress.city);
      setStateProvince(normalizedAddress.stateProvince);
      setPostalCode(normalizedAddress.postalCode);
      setCountry(normalizedAddress.country);
      setCoords({ lat: parsed.lat, lng: parsed.lng });

      // ✅ Register company
      const { companyId } = await registerCompany(
        companyName,
        industry.value,
        companySize.value,
        normalizedAddress,
        ownerId,
        timeZone.value,
        multiLocationEnabled,
      );

      // ✅ If multi-location enabled, add extra locations (optional)
      if (multiLocationEnabled && extraLocations.length) {
        const locationsRef = collection(
          db,
          "companies",
          companyId,
          "locations",
        );

        let added = 0;

        for (const loc of extraLocations) {
          if (!loc?.name?.trim()) continue;
          if (!loc?.street?.trim()) continue;

          const locAddress = {
            street: loc.street || "",
            city: loc.city || "",
            stateProvince: loc.stateProvince || "",
            postalCode: loc.postalCode || "",
            country: loc.country || "",
            geo:
              loc.coords?.lat != null && loc.coords?.lng != null
                ? { lat: loc.coords.lat, lng: loc.coords.lng }
                : null,
          };

          await addDoc(locationsRef, {
            name: loc.name.trim(),
            address: locAddress,
            isPrimary: false,
            active: true,
            createdAt: serverTimestamp(),
            createdBy: ownerId,
          });

          added++;
        }

        // Optional count
        await updateDoc(doc(db, "companies", companyId), {
          locationsCount: 1 + added,
        });
      }

      // ✅ Update user doc with correct companyId
      const userRef = doc(db, "users", ownerId);
      await updateDoc(userRef, { companyId });

      // Pilot check based on the employer email from Auth
      const employerEmail = (user?.email || "").toLowerCase();

      let pilotOK = false;
      let pilotDays = 60;

      try {
        const allow = await isPilotEmail(employerEmail);
        pilotOK = allow.ok;
        pilotDays = allow.trialDays || 60;
      } catch {
        // fall back to normal flow
      }
      let nextPath = "/subscription";
      let nextOnboardingStep = "subscription";

      if (pilotOK) {
        await ensureCompanyPilot(companyId, employerEmail, pilotDays);

        const pilotLimit = teamSizeToMax(companySize?.value);

        await updateDoc(doc(db, "companies", companyId), {
          "pilot.employeeLimit": pilotLimit,
        });

        // ✅ don't navigate yet — just decide where to go
        nextPath = "/admin-dashboard";
        nextOnboardingStep = "dashboard";
      }

      // ✅ wait until the overlay sequencing finishes
      await waitForSequenceToFinish();

      // ✅ NOW write onboarding (prevents route guards from redirecting early)
      await updateDoc(userRef, {
        onboarding: {
          step: nextOnboardingStep,
          emailVerified: true,
          companyCreated: true,
          subscriptionStatus: pilotOK ? "pilot" : "pending_selection",
          lastUpdatedAt: new Date(),
        },
      });
      trackEvent("company_setup_success", {
        industry: industry?.value || "unknown",
        team_size: companySize?.value || "unknown",
        time_zone: timeZone?.value || "unknown",
        multi_location: multiLocationEnabled,
        next_path: nextPath,
        pilot: pilotOK,
      });

      localStorage.removeItem(DRAFT_KEY);
      localStorage.setItem("justRegistered", "true");

      localStorage.removeItem("oiiko_setup_in_progress");
      localStorage.removeItem("oiiko_setup_started_at");

      // ✅ finally navigate
      navigate(nextPath);
    } catch (err) {
      localStorage.removeItem("oiiko_setup_in_progress");
      localStorage.removeItem("oiiko_setup_started_at");

      console.error("🔥 Company Setup Error:", err);
      trackEvent("company_setup_error", {
        error_code: err.code || err.message || "unknown",
        step,
      });
      const { title, message } = formatError("companySetup", err);
      setError({ title, message });
      setLoadingAnimationVisible(false);
      setSetupVisible(false);
    }
  };

  return (
    <div className="companysetup-page">
      <div className="companystyle">
        {/* LEFT: form */}
        <div className="companysetup-container">
          <h2>Welcome! Let’s Set Up Your Company</h2>
          <p>
            We’ll use these details to personalize your Oiiko experience and
            recommend the best plan for your team.
          </p>
          {setupVisible && (
            <div className="setup-overlay" role="alert" aria-live="polite">
              <div className="setup-overlay-card">
                <Lottie
                  animationData={loadingAnimation}
                  loop
                  style={{ height: 160 }}
                />

                <div className="setup-seq-title">Customizing your setup…</div>

                <div className="setup-seq-list">
                  {SETUP_SEQUENCE.map((txt, i) => {
                    const done = i < setupMsgIndex;
                    const active = i === setupMsgIndex;

                    return (
                      <div
                        key={txt}
                        className={`setup-seq-row ${done ? "done" : ""} ${
                          active ? "active" : ""
                        }`}
                      >
                        <span className="setup-seq-icon" aria-hidden="true">
                          {done ? (
                            <CheckCircle2
                              className="setup-ico done"
                              size={18}
                            />
                          ) : (
                            <Circle className="setup-ico" size={18} />
                          )}
                        </span>

                        <span className="setup-seq-text">{txt}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ✅ Step illustration (single-column layout) */}
          <div className="companysetup-hero">
            <img
              ref={heroImgRef}
              src={getIllustrationForStep(step)}
              alt=""
              aria-hidden="true"
              className="companysetup-hero-img"
            />
          </div>

          {/* Progress header */}
          <div className="setup-progress">
            <div className="setup-progress-track" aria-hidden="true">
              <div
                className="setup-progress-fill"
                style={{ width: `${(step / 6) * 100}%` }}
              />
            </div>
          </div>

          {/* Global error banner */}
          {error && (
            <StatusBanner
              type="error"
              title={error.title}
              message={error.message}
              onClose={() => setError(null)}
            />
          )}

          <div ref={contentRef}>
            <form onSubmit={handleSubmit} className="companysetup-form">
              {/* STEP 1 */}
              {/* =========================
    STEP 1 — Company Name
   ========================= */}
              {step === 1 && (
                <>
                  <label>What’s your company called?</label>
                  <p>
                    This will appear on your dashboard and employee profiles.
                  </p>

                  <input
                    type="text"
                    placeholder="Company name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    required
                  />

                  <div className="wizard-actions">
                    <button
                      type="button"
                      className="wizard-next"
                      onClick={goNext}
                      disabled={!step1Valid}
                    >
                      Continue
                    </button>
                  </div>
                </>
              )}

              {/* =========================
    STEP 2 — Industry
   ========================= */}
              {step === 2 && (
                <>
                  <label>What industry are you in?</label>
                  <p>
                    We’ll tailor tools and templates to how your team works.
                  </p>

                  <Select
                    options={industryOptions}
                    value={industry}
                    onChange={setIndustry}
                    placeholder="Select an industry"
                    classNamePrefix="react-select"
                  />

                  <div className="wizard-actions">
                    <button
                      type="button"
                      className="wizard-back"
                      onClick={goBack}
                    >
                      Back
                    </button>

                    <button
                      type="button"
                      className="wizard-next"
                      onClick={goNext}
                      disabled={!step2Valid}
                    >
                      Continue
                    </button>
                  </div>
                </>
              )}

              {/* =========================
    STEP 3 — Team Size
   ========================= */}
              {step === 3 && (
                <>
                  <label>How big is your team right now?</label>
                  <p>
                    We’ll recommend the best plan and setup for your team size.
                  </p>

                  <Select
                    options={teamSizeOptions}
                    value={companySize}
                    onChange={setCompanySize}
                    placeholder="Choose team size"
                    classNamePrefix="react-select"
                  />
                  {companySize?.value === "1-3" && (
                    <div className="free-plan-notice" role="status">
                      <div className="free-plan-notice-icon">✓</div>

                      <div>
                        <strong>
                          Your team qualifies for Oiiko’s Free Plan
                        </strong>

                        <p>
                          Manage up to 3 employees with scheduling, time
                          tracking, payroll-ready reports, and multi-location
                          support for $0.
                        </p>

                        <span>
                          Your team size will be automatically selected on the
                          next page.
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="wizard-actions">
                    <button
                      type="button"
                      className="wizard-back"
                      onClick={goBack}
                    >
                      Back
                    </button>

                    <button
                      type="button"
                      className="wizard-next"
                      onClick={goNext}
                      disabled={!step3Valid}
                    >
                      Continue
                    </button>
                  </div>
                </>
              )}

              {/* =========================
    STEP 4 — Primary Address
   ========================= */}
              {step === 4 && (
                <>
                  <div className="company-grid">
                    <div className="company-gridinput">
                      <label style={{ marginTop: "1rem" }}>
                        Where is your main office?
                      </label>
                      <p>
                        This helps with scheduling, reports, and time-off
                        accuracy.
                      </p>

                      <AddressAutocomplete
                        value={street}
                        onChange={(val) => setStreet(val)}
                        onSelect={(place) => {
                          if (!place) return;
                          const parsed = parsePlace(place);
                          setStreet(parsed.street || "");
                          setCity(parsed.city || "");
                          setStateProvince(parsed.stateProvince || "");
                          setPostalCode(parsed.postalCode || "");
                          setCountry(parsed.country || "");
                          setCoords({ lat: parsed.lat, lng: parsed.lng });
                        }}
                        placeholder="Start typing your address..."
                      />
                    </div>

                    <div className="company-gridinput">
                      <label>City</label>
                      <input
                        type="text"
                        placeholder="City"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                      />
                    </div>

                    <div className="company-gridinput">
                      <label>State / Province</label>
                      <input
                        type="text"
                        placeholder="State or Province"
                        value={stateProvince}
                        onChange={(e) => setStateProvince(e.target.value)}
                        required
                      />
                    </div>

                    <div className="company-gridinput">
                      <label>Postal Code</label>
                      <input
                        type="text"
                        placeholder="Postal Code"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        required
                      />
                    </div>

                    <div className="company-gridinput">
                      <label>Country</label>
                      <input
                        type="text"
                        placeholder="Country"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="wizard-actions">
                    <button
                      type="button"
                      className="wizard-back"
                      onClick={goBack}
                    >
                      Back
                    </button>

                    <button
                      type="button"
                      className="wizard-next"
                      onClick={goNext}
                      disabled={!step4Valid}
                    >
                      Continue
                    </button>
                  </div>
                </>
              )}

              {/* =========================
    STEP 5 — Time Zone
   ========================= */}
              {step === 5 && (
                <>
                  <label>What time zone does your team operate in?</label>
                  <p>
                    All schedules, reports, and time tracking will follow this
                    time zone.
                  </p>

                  <Select
                    options={timeZoneOptions}
                    value={timeZone}
                    onChange={(selected) => setTimeZone(selected)}
                    placeholder="Choose a time zone"
                    classNamePrefix="react-select"
                  />

                  <div className="wizard-actions">
                    <button
                      type="button"
                      className="wizard-back"
                      onClick={goBack}
                    >
                      Back
                    </button>

                    <button
                      type="button"
                      className="wizard-next"
                      onClick={goNext}
                      disabled={!step5Valid}
                    >
                      Continue
                    </button>
                  </div>
                </>
              )}

              {/* =========================
    STEP 6 — Multiple Locations Toggle
   ========================= */}
              {step === 6 && (
                <>
                  {/* Toggle */}
                  <div className="companysetup-section">
                    <div className="multi-toggle-row">
                      <div className="multi-toggle-label">
                        <strong>
                          Do you operate in more than one location?
                        </strong>
                        <span>
                          Great for teams with multiple offices, stores, or job
                          sites.
                        </span>
                      </div>

                      <label className="companysetup-toggle">
                        <input
                          type="checkbox"
                          checked={multiLocationEnabled}
                          onChange={(e) =>
                            setMultiLocationEnabled(e.target.checked)
                          }
                        />
                        <span className="slider" />
                      </label>
                    </div>
                  </div>

                  {/* 👇 Conditional extra locations UI */}
                  {multiLocationEnabled && (
                    <div
                      ref={locationsRef}
                      className="companysetup-section"
                      style={{ marginTop: 12 }}
                    >
                      <div className="locations-header">
                        <div>
                          <h3>Add your other locations</h3>
                          <p>
                            Optional — you can skip this for now and add them
                            later in Settings.
                          </p>
                        </div>

                        <button
                          type="button"
                          className="btn-light"
                          onClick={() =>
                            setExtraLocations((prev) => [
                              ...prev,
                              {
                                name: "",
                                street: "",
                                city: "",
                                stateProvince: "",
                                postalCode: "",
                                country: "",
                                coords: { lat: null, lng: null },
                              },
                            ])
                          }
                        >
                          + Add Location
                        </button>
                      </div>

                      {extraLocations.map((loc, idx) => (
                        <div key={idx} className="location-card">
                          <div className="location-card-head">
                            <h4>Location {idx + 1}</h4>
                            <button
                              type="button"
                              className="btn-ghost"
                              onClick={() =>
                                setExtraLocations((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                )
                              }
                            >
                              Remove
                            </button>
                          </div>

                          <label>Location Name</label>
                          <input
                            value={loc.name}
                            onChange={(e) => {
                              const v = e.target.value;
                              setExtraLocations((prev) =>
                                prev.map((x, i) =>
                                  i === idx ? { ...x, name: v } : x,
                                ),
                              );
                            }}
                            placeholder="e.g., Downtown Store"
                          />

                          <label>Address</label>
                          <AddressAutocomplete
                            value={loc.street}
                            onChange={(val) => {
                              setExtraLocations((prev) =>
                                prev.map((x, i) =>
                                  i === idx ? { ...x, street: val } : x,
                                ),
                              );
                            }}
                            onSelect={(place) => {
                              if (!place) return;
                              const p = parsePlace(place);

                              setExtraLocations((prev) =>
                                prev.map((x, i) =>
                                  i === idx
                                    ? {
                                        ...x,
                                        street: p.street || "",
                                        city: p.city || "",
                                        stateProvince: p.stateProvince || "",
                                        postalCode: p.postalCode || "",
                                        country: p.country || "",
                                        coords: { lat: p.lat, lng: p.lng },
                                      }
                                    : x,
                                ),
                              );
                            }}
                            placeholder="Start typing location address..."
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="wizard-actions">
                    <button
                      type="button"
                      className="wizard-back"
                      onClick={goBack}
                    >
                      Back
                    </button>

                    <button type="submit" className="wizard-next">
                      Create Company
                    </button>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>

        {/* RIGHT: animation + headline */}
        {/* RIGHT: Illustration panel (replaces Lottie) */}
      </div>
    </div>
  );
}
